"use client"

import { useState, useEffect, useRef } from "react"
import {ChevronLeft, ChevronRight, UserPlus, Link, CheckCircle, Rocket, Info, Sparkles} from "lucide-react"
import { Button } from "react-bootstrap"
import { useLocation, useNavigate } from "react-router-dom"
import { translateToEnglish, translateToSpanish } from "../../utils/translations"
import InfoModal from "./InfoModal"

const slides = [
    {
        title: "Gracias por unirte a CONTACTAME",
        content:
            "¡Bienvenido! Ahora formás parte de una comunidad donde mostrar tu trabajo y conectar con nuevos clientes es más simple que nunca.",
        icon: <Sparkles size={56} className="slide-icon" />,
        color: "#cc1e00",
        gradient: "linear-gradient(135deg, #cc1e00, #ff4444)",
    },
    {
        title: "Tu perfil único",
        content:
            "Vas a tener tu perfil con toda tu información y formas de contacto. Ideal para compartir en redes, grupos o con futuros clientes.",
        icon: <UserPlus size={56} className="slide-icon" />,
        color: "#0056b3",
        gradient: "linear-gradient(135deg, #0056b3, #0080ff)",
    },
    {
        title: "Proceso de verificación",
        content:
            "Nuestro equipo revisará tus datos para garantizar confianza en la plataforma. Te informaremos cuando el perfil sea activado.",
        icon: <CheckCircle size={56} className="slide-icon" />,
        color: "#28a745",
        gradient: "linear-gradient(135deg, #28a745, #34ce57)",
    },
    {
        title: "¿Listo para crecer juntos?",
        content: "Completá tu perfil en menos de 5 minutos y activá tu suscripción gratuita ¡Empecemos!",
        icon: <Rocket size={56} className="slide-icon" />,
        color: "#6f42c1",
        gradient: "linear-gradient(135deg, #6f42c1, #8e5bdc)",
    },
]

const IntroCarousel = ({ onComplete, providerType }) => {
    const [currentSlide, setCurrentSlide] = useState(0)
    const [isAutoPlaying, setIsAutoPlaying] = useState(true)
    const [touchStartX, setTouchStartX] = useState(0)
    const [touchStartY, setTouchStartY] = useState(0)
    const [isDragging, setIsDragging] = useState(false)
    const [dragOffset, setDragOffset] = useState(0)
    const [showInfoModal, setShowInfoModal] = useState(false)
    const carouselRef = useRef(null)
    const slideTimerRef = useRef(null)
    const navigate = useNavigate()
    const location = useLocation()

    // Auto-play mejorado con pausa en hover
    useEffect(() => {
        if (isAutoPlaying && currentSlide < slides.length - 1) {
            slideTimerRef.current = setTimeout(() => {
                setCurrentSlide((prevSlide) => prevSlide + 1)
            }, 6000)
        }

        return () => {
            if (slideTimerRef.current) {
                clearTimeout(slideTimerRef.current)
            }
        }
    }, [currentSlide, isAutoPlaying])

    const nextSlide = () => {
        if (currentSlide < slides.length - 1) {
            setCurrentSlide(currentSlide + 1)
            pauseAutoPlay()
        }
    }

    const prevSlide = () => {
        if (currentSlide > 0) {
            setCurrentSlide(currentSlide - 1)
            pauseAutoPlay()
        }
    }

    const goToSlide = (index) => {
        setCurrentSlide(index)
        pauseAutoPlay()
    }

    const pauseAutoPlay = () => {
        setIsAutoPlaying(false)
        if (slideTimerRef.current) {
            clearTimeout(slideTimerRef.current)
        }
        // Reanudar después de 10 segundos
        setTimeout(() => setIsAutoPlaying(true), 10000)
    }

    // Manejo táctil mejorado
    const handleTouchStart = (e) => {
        const touch = e.touches[0]
        setTouchStartX(touch.clientX)
        setTouchStartY(touch.clientY)
        setIsDragging(true)
        pauseAutoPlay()
    }

    const handleTouchMove = (e) => {
        if (!isDragging) return

        const touch = e.touches[0]
        const deltaX = touch.clientX - touchStartX
        const deltaY = Math.abs(touch.clientY - touchStartY)

        // Solo procesar si es un gesto horizontal predominante
        if (Math.abs(deltaX) > deltaY && Math.abs(deltaX) > 10) {
            e.preventDefault()
            // Limitar el drag
            const maxDrag = carouselRef.current?.offsetWidth * 0.3 || 100
            setDragOffset(Math.max(-maxDrag, Math.min(maxDrag, deltaX)))
        }
    }

    const handleTouchEnd = () => {
        if (!isDragging) return

        setIsDragging(false)
        const threshold = 60

        if (Math.abs(dragOffset) > threshold) {
            if (dragOffset > 0 && currentSlide > 0) {
                prevSlide()
            } else if (dragOffset < 0 && currentSlide < slides.length - 1) {
                nextSlide()
            }
        }

        setDragOffset(0)
    }

    const handleComplete = () => {
        if (translateToEnglish(providerType) === "service") {
            navigate("/registro/servicios")
        } else if (translateToEnglish(providerType) === "products") {
            navigate("/registro/productos")
        }
        onComplete()
    }

    const progressPercentage = ((currentSlide + 1) / slides.length) * 100

    return (
        <div className="intro-carousel-simple">
            <InfoModal
                isOpen={showInfoModal}
                onClose={() => setShowInfoModal(false)}
            />

            {/* Barra de progreso */}
            <div className="progress-container">
                <div className="progress-track">
                    <div
                        className="progress-fill"
                        style={{
                            width: `${progressPercentage}%`,
                            background: slides[currentSlide].gradient
                        }}
                    />
                </div>
                <span className="progress-text">
                    {currentSlide + 1} de {slides.length}
                </span>
            </div>

            {/* Contenedor de slides */}
            <div
                className="slides-container"
                ref={carouselRef}
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
                onMouseEnter={() => setIsAutoPlaying(false)}
                onMouseLeave={() => setIsAutoPlaying(true)}
            >
                <div
                    className="slides-track"
                    style={{
                        transform: `translateX(${(-currentSlide * 100) + (isDragging ? (dragOffset / (carouselRef.current?.offsetWidth || 1) * 100) : 0)}%)`,
                        transition: isDragging ? 'none' : 'transform 0.5s ease'
                    }}
                >
                    {slides.map((slide, index) => (
                        <div
                            key={index}
                            className={`slide ${index === currentSlide ? "active" : ""}`}
                        >
                            <div
                                className="slide-background"
                                style={{ background: slide.gradient }}
                            />

                            <div className="slide-content">
                                <div
                                    className="icon-container"
                                    style={{
                                        background: slide.gradient,
                                        boxShadow: `0 20px 40px ${slide.color}40`
                                    }}
                                >
                                    {slide.icon}
                                </div>

                                <div className="slide-text">
                                    <h2 className="slide-title">{slide.title}</h2>
                                    <p className="slide-description">{slide.content}</p>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Controles de navegación */}
            <div className="controls">
                <button
                    onClick={prevSlide}
                    className="control-btn prev"
                    disabled={currentSlide === 0}
                    aria-label="Slide anterior"
                >
                    <ChevronLeft size={24} />
                </button>

                {/* Indicadores */}
                <div className="indicators">
                    {slides.map((slide, index) => (
                        <button
                            key={index}
                            onClick={() => goToSlide(index)}
                            className={`indicator ${index === currentSlide ? "active" : ""}`}
                            style={{
                                background: index === currentSlide ? slide.gradient : 'transparent'
                            }}
                            aria-label={`Ir al slide ${index + 1}`}
                        />
                    ))}
                </div>

                <button
                    onClick={nextSlide}
                    className="control-btn next"
                    disabled={currentSlide === slides.length - 1}
                    aria-label="Siguiente slide"
                >
                    <ChevronRight size={24} />
                </button>
            </div>

            {/* Botones de acción en el último slide */}
            {currentSlide === slides.length - 1 && (
                <div className="action-buttons">
                    <button
                        onClick={() => setShowInfoModal(true)}
                        className="intro-carousel-preview-btn"
                    >
                        <Info size={20} />
                        <span>Ver previsualización</span>
                    </button>

                    <button
                        onClick={handleComplete}
                        className="intro-carousel-start-btn"
                        style={{ background: slides[currentSlide].gradient }}
                        aria-label="Comenzar registro"
                    >
                        <span>¡Empezar ahora!</span>
                        <Rocket size={20} />
                    </button>
                </div>
            )}

        </div>
    )
}

export default IntroCarousel